import React, { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, X, ChevronLeft, ChevronRight, Eye, Users } from "lucide-react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";

const cell = (v) => (v && v !== "N/A" ? v : v === 0 ? "0" : v || "—");

export default function EJagritiResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state?.data;
  const summary = location.state?.summary;

  const allResults = useMemo(() => data?.results || [], [data]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allResults;
    return allResults.filter((r) =>
      [r.caseNumber, r.complainant, r.respondent, r.caseStage, r.caseCategory, r.filingReferenceNumber]
        .some((f) => typeof f === "string" && f.toLowerCase().includes(q))
    );
  }, [allResults, query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const totalFound = data?.pagination?.totalRecords ?? allResults.length;

  const openCase = (caseNumber) =>
    navigate("/portal/ejagriti/case", { state: { caseNumber, initialTab: "status" } });

  const goToPage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearSearch = () => {
    setQuery("");
    setPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + maxPagesToShow - 1);
    if (end - start < maxPagesToShow - 1) start = Math.max(1, end - maxPagesToShow + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <main className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="mt-4"><FindCaseStepper current={3} /></div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white py-12 text-center shadow-sm">
            <p className="text-base font-medium text-slate-700">No search results to display.</p>
            <Link
              to="/portal/fetchfromejagriti"
              className="mt-3 inline-block text-sm font-medium text-yellow-600 hover:underline"
            >
              Go to e-Jagriti Search
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromejagriti" className="text-slate-500 hover:text-slate-800">Consumer Court (e-Jagriti)</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Results</span>
        </div>

        {/* Stepper */}
        <div className="mt-4"><FindCaseStepper current={3} /></div>

        {/* Summary */}
        <p className="mt-4 text-sm text-slate-500">
          About <span className="font-semibold text-slate-800">{totalFound.toLocaleString()}</span> results
          {summary?.query && <> for <span className="font-medium text-slate-800">&ldquo;{summary.query}&rdquo;</span></>}
          {summary?.commissionType && (
            <span> · {summary.commissionType}{summary.stateName ? ` · ${summary.stateName}` : ""}</span>
          )}
        </p>

        {/* Stat card */}
        {allResults.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:max-w-xs">
            <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
                <Users className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold leading-tight text-slate-900">{totalFound.toLocaleString()}</p>
                <p className="truncate text-xs text-slate-500">Total Found</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Card */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search in results..."
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Table / empty states */}
          {total === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">
                {query ? "No cases match your search" : "No cases found"}
              </p>
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="w-14 px-4 py-3">Sl.No.</th>
                    <th className="px-4 py-3">Case Number</th>
                    <th className="px-4 py-3">Complainant</th>
                    <th className="px-4 py-3">Respondent</th>
                    <th className="px-4 py-3">Case Stage</th>
                    <th className="px-4 py-3">Case Category</th>
                    <th className="px-4 py-3">Filing Date</th>
                    <th className="px-4 py-3">Last Hearing</th>
                    <th className="px-4 py-3">Next Hearing</th>
                    <th className="w-16 px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, idx) => {
                    const openThis = () => openCase(r.caseNumber);
                    return (
                      <tr
                        key={`${r.filingReferenceNumber || r.caseNumber}-${idx}`}
                        className="border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50"
                      >
                        <td className="px-4 py-3.5 text-sm text-slate-500">{(page - 1) * pageSize + idx + 1}</td>
                        <td className="px-4 py-3.5 text-sm font-medium text-slate-900 whitespace-nowrap">
                          <button onClick={openThis} className="text-left hover:text-yellow-600 hover:underline">
                            {cell(r.caseNumber)}
                          </button>
                        </td>
                        <td className="min-w-[180px] px-4 py-3.5 text-sm text-slate-600">{cell(r.complainant)}</td>
                        <td className="min-w-[180px] px-4 py-3.5 text-sm text-slate-600">{cell(r.respondent)}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">{cell(r.caseStage)}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">{cell(r.caseCategory)}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">{cell(r.filingDate)}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">{cell(r.lastHearingDate)}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">{cell(r.nextHearingDate)}</td>
                        <td className="px-4 py-3.5 text-center">
                          <button
                            type="button"
                            onClick={openThis}
                            title="View case details"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer — result range + pagination */}
          {total > 0 && (
            <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} results
              </p>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {page > 3 && (
                    <>
                      <button
                        onClick={() => goToPage(1)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        1
                      </button>
                      {page > 4 && <span className="px-1 text-slate-400">...</span>}
                    </>
                  )}

                  {getPageNumbers().map((n) => (
                    <button
                      key={n}
                      onClick={() => goToPage(n)}
                      className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                        page === n
                          ? "bg-yellow-500 text-slate-900"
                          : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}

                  {page < totalPages - 2 && (
                    <>
                      {page < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                      <button
                        onClick={() => goToPage(totalPages)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
